import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CmsPageContentType } from '@rd/types';
import {
  catchError,
  concatMap,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
} from 'rxjs';
import { CmsApiService } from '../services/cms-api.service';
import {
  CmsGetRefKeyFailed,
  CmsGetRefKeySuccess,
  CmsLoadPageContent,
  CmsLoadPageContentFailed,
  CmsLoadPageContentSuccess,
} from './cms.actions';
import { getCmsRef } from './cms.selector';

@Injectable()
export class CmsEffects {
  constructor(
    private actions$: Actions,
    private cmsApiService: CmsApiService,
    private store: Store,
  ) {}

  refKey$: Observable<string | null> = this.store.select(getCmsRef).pipe(
    switchMap((ref) => {
      if (!!ref) {
        return of(ref);
      }
      return this.cmsApiService.getApiRef().pipe(
        map((ref) => {
          this.store.dispatch(CmsGetRefKeySuccess({ ref }));
          return ref!;
        }),
        catchError((error) => {
          this.store.dispatch(CmsGetRefKeyFailed({ error }));
          return of(null);
        }),
      );
    }),
    take(1),
  );

  cmsLoadPageContent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CmsLoadPageContent),
      concatMap((action) => this.refKey$.pipe(map((ref) => ({ action, ref })))),
      mergeMap(({ action, ref }) => {
        const { pageType, pageName, locale } = action;
        if (!ref) {
          return [
            CmsLoadPageContentFailed({
              pageName,
              error: new Error('Failed to fetch Prismic Ref key'),
            }),
          ];
        }

        switch (pageType) {
          case 'campaignbanner':
            return this.cmsApiService
              .getCampaignBanner(pageName, locale, ref)
              .pipe(
                map((response) =>
                  this.transformContentSuccess(response, pageName),
                ),
              );
          case 'pagecontent':
            return this.cmsApiService
              .getPageContent(pageName, locale, ref)
              .pipe(
                map((response) => {
                  return this.transformContentSuccess(response, pageName);
                }),
              );
          case 'terms':
            return this.cmsApiService
              .getTermsContent(pageName, locale, ref)
              .pipe(
                map((response) =>
                  this.transformContentSuccess(response, pageName),
                ),
              );
          case 'featuredpartners':
            return this.cmsApiService
              .getFeaturedPartners(pageName, locale, ref)
              .pipe(
                map((response) =>
                  this.transformContentSuccess(response, pageName),
                ),
              );
          case 'feature_partner_v2':
            return this.cmsApiService
              .getFeaturedPartnersV2(pageName, locale, ref)
              .pipe(
                map((response) =>
                  this.transformContentSuccess(response, pageName),
                ),
              );
          case 'campaignbannermultiple':
            return this.cmsApiService
              .getCampaignBanners(pageName, locale, ref)
              .pipe(
                map((response) =>
                  this.transformContentSuccess(response, pageName),
                ),
              );
          case 'alerts': {
            return this.cmsApiService
              .getAlerts(pageName, locale, ref)
              .pipe(
                map((response) =>
                  this.transformContentSuccess(response, pageName),
                ),
              );
          }
          case 'campaignpage':
          case 'promopage':
          case 'campaignpage-variant1':
            return this.cmsApiService
              .getFullPage(pageName, locale, pageType, ref)
              .pipe(
                map((response) =>
                  this.transformContentSuccess(response, pageName),
                ),
              );
          default:
            return [
              CmsLoadPageContentFailed({
                pageName,
                error: new Error('Page type not found'),
              }),
            ];
        }
      }),
    ),
  );

  private transformContentSuccess(
    data: CmsPageContentType | null,
    pageName: string,
  ):
    | ReturnType<typeof CmsLoadPageContentSuccess>
    | ReturnType<typeof CmsLoadPageContentFailed> {
    if (data) {
      return CmsLoadPageContentSuccess({
        pageName: pageName,
        data: data,
      });
    }

    return CmsLoadPageContentFailed({
      pageName,
      error: new Error('Page content is null'),
    });
  }
}
